import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environment/environment';
import { extraireReponse } from './api';
import { Authentification } from './authentification';
import { ModeAffichageGraphique } from './installation';
import { ReponseApi } from './EntiteProjet';
import { ParcProductionCumuleeDonnees, ParcProductionCumuleeResponse } from './parc-production-cumulee.types';

@Injectable({
  providedIn: 'root',
})
export class ParcProductionCumuleeService {
  private readonly auth = inject(Authentification);
  private readonly http = inject(HttpClient);

  getProductionCumuleeRange(mode: ModeAffichageGraphique = 'mois', fin?: Date) {
    const ref = fin ? new Date(fin) : new Date();
    const annee = ref.getFullYear();
    const mois = String(ref.getMonth() + 1).padStart(2, '0');

    switch (mode) {
      case 'jour':
        return { format: 'jour', debutApi: `${annee}-${mois}` };
      case 'annee':
        return { format: 'an', debutApi: String(annee) };
      default:
        return { format: 'mois', debutApi: String(annee) };
    }
  }

  async getParcProductionCumulee(
    parcId: number,
    mode: ModeAffichageGraphique = 'mois',
    fin?: Date,
  ): Promise<ParcProductionCumuleeDonnees> {
    const { debutApi, format } = this.getProductionCumuleeRange(mode, fin);
    const url = `${environment.apiUrl}/parc/${parcId}/indicateurs`;
    const params = new HttpParams()
      .set('type', 'production-cumulee')
      .set('format', format)
      .set('debut', debutApi);

    const res = await firstValueFrom(
      this.http.get<ReponseApi<ParcProductionCumuleeResponse>>(url, { ...this.auth.prepareHeaders(), params })
    );
    const reponse = extraireReponse(res);

    const vide: ParcProductionCumuleeDonnees = {
      data: [],
      labels: {
        production: { libelle: 'Production cumulée', unite: 'MWh' },
        productibleEstime: { libelle: 'Productible contractuel cumulé', unite: 'MWh' },
      },
    };
    if (!reponse) return vide;

    return {
      data: (reponse.datas ?? []).map((ligne) => ({
        date: String(ligne.date ?? ligne.periode ?? ligne.debut ?? ''),
        production: Number(ligne.production ?? ligne.production_cumulee ?? ligne.prod ?? 0),
        productibleEstime: Number(
          ligne.prod_calc ?? ligne.reference ?? ligne.reference_cumulee ?? ligne.ref ?? 0
        ),
      })).filter((ligne) => ligne.date.length > 0),
      labels: {
        production: {
          libelle: reponse.champs?.production?.libelle ?? 'Production cumulée',
          unite: reponse.champs?.production?.unite ?? 'MWh',
        },
        productibleEstime: {
          libelle: reponse.champs?.prod_calc?.libelle ?? 'Productible contractuel cumulé',
          unite: reponse.champs?.prod_calc?.unite ?? 'MWh',
        },
      },
    };
  }
}
